import {
  type IconAlarm,
  IconAlertTriangle,
  IconCalendarEvent,
  IconClockDollar,
  IconListDetails,
  IconReceipt,
} from '@tabler/icons-react';
import { i18nKeys } from 'locales';
import type { TemplateV2 } from 'shared/hooks/use-load-template-v2';

type TablerIcon = React.ComponentType<React.ComponentProps<typeof IconAlarm>>;

// Step types that never carry a statement of account: invoice/credit_note attach the
// document itself (PDF/UBL), and these reminder types are always sent on their own.
const STEP_TYPES_WITHOUT_STATEMENT = [
  'invoice',
  'credit_note',
  'formal_notice',
  'additional_reminder',
];

export const canSelectStatementType = (template: TemplateV2) =>
  template.planType !== 'payment' && !STEP_TYPES_WITHOUT_STATEMENT.includes(template.stepType);

export const getStatementTypeOptions = (template: TemplateV2) =>
  template.stepType === 'preventive_reminder'
    ? PREVENTIVE_STATEMENT_TYPE_OPTIONS
    : STATEMENT_TYPE_OPTIONS;

// Mirrors the backend `Constants::Templates::GROUPED_TYPES`. `null` means "no statement".
export type StatementType =
  | 'unpaid_invoices'
  | 'overdue_invoices'
  | 'debtor_balance'
  | 'overdue_debtor_balance';

export type StatementTypeOption = {
  value: StatementType;
  Icon: TablerIcon;
  titleKey: string;
  descriptionKey: string;
};

const STATEMENT_TYPE_OPTIONS: ReadonlyArray<StatementTypeOption> = [
  {
    value: 'unpaid_invoices',
    Icon: IconReceipt,
    titleKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OPEN_INVOICES_TITLE,
    descriptionKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OPEN_INVOICES_DESCRIPTION,
  },
  {
    value: 'overdue_invoices',
    Icon: IconAlertTriangle,
    titleKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OVERDUE_INVOICES_TITLE,
    descriptionKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OVERDUE_INVOICES_DESCRIPTION,
  },
  {
    value: 'debtor_balance',
    Icon: IconListDetails,
    titleKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.FULL_BALANCE_TITLE,
    descriptionKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.FULL_BALANCE_DESCRIPTION,
  },
  {
    value: 'overdue_debtor_balance',
    Icon: IconClockDollar,
    titleKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OVERDUE_BALANCE_TITLE,
    descriptionKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.OVERDUE_BALANCE_DESCRIPTION,
  },
];

// Any grouped type makes the backend group a debtor's preventive reminders that share a
// due date; the overdue ones would find nothing since these invoices are not due yet.
const PREVENTIVE_STATEMENT_TYPE_OPTIONS: ReadonlyArray<StatementTypeOption> = [
  {
    value: 'unpaid_invoices',
    Icon: IconCalendarEvent,
    titleKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.PREVENTIVE_SAME_DAY_TITLE,
    descriptionKey: i18nKeys.TEMPLATE.STATEMENT_OF_ACCOUNT.TYPES.PREVENTIVE_SAME_DAY_DESCRIPTION,
  },
];
